import { Component, ElementRef, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { COMMA, ENTER } from "@angular/cdk/keycodes";
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { map, Observable, startWith } from "rxjs";
import { MatAutocomplete, MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";
import { TranslateService } from "@ngx-translate/core";
import { Diagnosis, diagnosisCategories, DiagnosisCategory, DiagnosisDTO } from "../../models";

@Component({
  selector: 'calingo-fnol-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AutocompleteComponent {
  @ViewChild('inputElement', { static: false })
  inputElementElementRef!: ElementRef<HTMLInputElement>;
  @ViewChild('auto', { static: false }) matAutocomplete!: MatAutocomplete;

  visible = true;
  removable = true;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  filteredValues: Observable<DiagnosisCategory[]>;
  selectedValues: DiagnosisDTO[] = [];
  allValues: DiagnosisCategory[] = [];
  autocompleteInputCtrl = new FormControl('', [this.validateDiagnosis()]);

  constructor(private translateService: TranslateService) {
    this.translateAllDiagnosis();

    this.translateService.onLangChange.subscribe(() => {
      this.translateAllDiagnosis();

      // This is a hack to force the autocomplete to update its values
      this.autocompleteInputCtrl.setValue(this.autocompleteInputCtrl.value);
    });

    this.filteredValues = this.autocompleteInputCtrl.valueChanges.pipe(
      startWith(null),
      map((value: string | null) =>
        this._filter(value || '')
      )
    );
  }

  @Input() set values(values: DiagnosisDTO[]) {
    this.selectedValues = values;
  }

  reset() {
    this.selectedValues = [];
    this.autocompleteInputCtrl.setValue(null);
  }

  validateDiagnosis(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return this.selectedValues.length === 0 ? { required: true } : null;
    }
  }

  translateAllDiagnosis() {
    this.allValues = diagnosisCategories.map(
      (category) => {
        return {
          ...category,
          // @ts-ignore
          displayName: category.translations[this.translateService.getCurrentLang()!] || category.displayName,
          values: category.values.map(
            (diagnosis) => {
              return {
                ...diagnosis,
                // @ts-ignore
                displayName: diagnosis.translations[this.translateService.getCurrentLang()!] || diagnosis.displayName
              }
            }
          )
        }
      }
    );
  }

  remove(value: DiagnosisDTO): void {
    const index = this.selectedValues.indexOf(value);

    if (index >= 0) {
      this.selectedValues.splice(index, 1);
    }

    this.autocompleteInputCtrl.setValue(this.autocompleteInputCtrl.value);
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.selectedValues.push(event.option.value);
    this.autocompleteInputCtrl.setValue(null);
  }

  displayChip(value: DiagnosisDTO): string {
    return this.allValues.find((category) => category.name === value.category)?.values.find((diagnosis) => diagnosis.id === value.id)?.displayName || '';
  }

  private _filter(value: string): DiagnosisCategory[] {
    if (typeof value !== 'string') {
      return this.allValues.slice();
    }
    const filterValue = value.toLowerCase();

    return this.allValues.map(
      (category) => {
        return {
          ...category,
          values: category.values.filter((diagnosis) => this.showDiagnosisForAutocomplete(diagnosis, filterValue))
        }
      }
    ).filter((category) => category.values.length > 0);
  }

  private showDiagnosisForAutocomplete(diagnosis: Diagnosis, filterValue: string): boolean {
    const alreadySelected = !!this.selectedValues.find(s => s.id === diagnosis.id && s.category === diagnosis.category);
    if (alreadySelected) return false;
    return diagnosis.id === 'sonstige' || diagnosis.displayName.toLowerCase().includes(filterValue);
  }
}
